import { defaultHomepageSettings } from '../data/mockData';
import type { HomepageSettings } from '../types';

export const DEFAULT_BRAND_FAVICON = '/favicon.svg';

/** iOS / Android home screen requires PNG (SVG is not used reliably). Generated from favicon.svg. */
export const PWA_APPLE_TOUCH_ICON = '/apple-touch-icon.png';

export type SiteBranding = HomepageSettings['branding'];

function getFallbackPlans(): HomepageSettings['pricing']['plans'] {
  try {
    const raw = localStorage.getItem('server_plans_cache');
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length > 0) return arr;
    }
  } catch {}
  return Array.isArray(defaultHomepageSettings.pricing?.plans)
    ? defaultHomepageSettings.pricing.plans
    : [];
}

/** Deep-merge partial homepage settings onto defaults so admin UI and public site never lose sections. */
export function mergeHomepageSettings(
  serverHp: Record<string, unknown> | null | undefined,
): HomepageSettings {
  const def = defaultHomepageSettings;
  const hp = (serverHp || {}) as Record<string, unknown>;
  const defPreviews = (def as HomepageSettings & { previews?: Record<string, unknown> }).previews || {};
  const hpPreviews = (hp.previews as Record<string, unknown>) || {};
  const serverPricing = (hp.pricing as Record<string, unknown>) || {};
  const serverPlans = serverPricing.plans;
  const cardButtons = (hp.cardButtons as Record<string, unknown>) || {};
  const serverButtons = cardButtons.buttons;

  return {
    ...def,
    ...hp,
    branding: {
      ...def.branding,
      ...((hp.branding as Partial<SiteBranding>) || {}),
    },
    hero: { ...def.hero, ...((hp.hero as object) || {}) },
    about: {
      ...def.about,
      ...((hp.about as object) || {}),
      benefits:
        Array.isArray((hp.about as { benefits?: unknown[] })?.benefits) &&
        (hp.about as { benefits: unknown[] }).benefits.length > 0
          ? (hp.about as { benefits: unknown[] }).benefits
          : def.about.benefits || [],
    },
    mission: {
      ...def.mission,
      ...((hp.mission as object) || {}),
      stats:
        (hp.mission as { stats?: unknown[] })?.stats || def.mission.stats || [],
    },
    features: {
      ...def.features,
      ...((hp.features as object) || {}),
      features:
        Array.isArray((hp.features as { features?: unknown[] })?.features) &&
        (hp.features as { features: unknown[] }).features.length > 0
          ? (hp.features as { features: unknown[] }).features
          : def.features.features || [],
    },
    pricing: {
      ...def.pricing,
      ...serverPricing,
      plans:
        Array.isArray(serverPlans) && serverPlans.length > 0
          ? (serverPlans as HomepageSettings['pricing']['plans'])
          : getFallbackPlans(),
    },
    partners: {
      ...def.partners,
      ...((hp.partners as object) || {}),
      logos:
        (hp.partners as { logos?: unknown[] })?.logos || def.partners.logos || [],
    },
    contact: {
      ...def.contact,
      ...((hp.contact as object) || {}),
      fields:
        (hp.contact as { fields?: unknown[] })?.fields || def.contact.fields || [],
    },
    footer: {
      ...def.footer,
      ...((hp.footer as object) || {}),
      links:
        (hp.footer as { links?: unknown[] })?.links || def.footer.links || [],
    },
    cardButtons: {
      ...def.cardButtons,
      ...cardButtons,
      buttons:
        Array.isArray(serverButtons) && serverButtons.length > 0
          ? serverButtons
          : def.cardButtons?.buttons || [],
    },
    maintenance: { ...def.maintenance, ...((hp.maintenance as object) || {}) },
    privacy: { ...def.privacy, ...((hp.privacy as object) || {}) },
    terms: { ...def.terms, ...((hp.terms as object) || {}) },
    styling: { ...def.styling, ...((hp.styling as object) || {}) },
    emailOTP: { ...(def as any).emailOTP, ...((hp.emailOTP as object) || {}) },
    apkDownload: { ...((def as any).apkDownload || {}), ...((hp.apkDownload as object) || {}) },
    previews: {
      ...defPreviews,
      ...hpPreviews,
      site: { ...(defPreviews.site as object), ...((hpPreviews.site as object) || {}) },
      card: { ...(defPreviews.card as object), ...((hpPreviews.card as object) || {}) },
      miniWebsite: { ...(defPreviews.miniWebsite as object), ...((hpPreviews.miniWebsite as object) || {}) },
      crm: { ...(defPreviews.crm as object), ...((hpPreviews.crm as object) || {}) },
      digitalCard: { ...(defPreviews.digitalCard as object), ...((hpPreviews.digitalCard as object) || {}) },
      templates: { ...(defPreviews.templates as object), ...((hpPreviews.templates as object) || {}) },
      contact: { ...(defPreviews.contact as object), ...((hpPreviews.contact as object) || {}) },
    },
  } as HomepageSettings;
}

export function resolveBrandFaviconUrl(
  branding?: { logoUrl?: string } | null,
  siteSeo?: { faviconUrl?: string } | null,
): string {
  const url = String(siteSeo?.faviconUrl || '').trim();
  if (url) return url;
  return DEFAULT_BRAND_FAVICON;
}
